import { db } from "services/firebase";

/**
 * Authored lookup Q&A help text — separate from trainingDecks (orientation) and
 * helpVideos (video catalog).
 *
 * Docs live in `helpContent/{contentId}`.
 * fde-t is excluded until Drift #3 scope is resolved (continuity doc §12a).
 */
export const HELP_CONTENT_ROLES = [
  "fde-c",
  "fde-dc",
  "professional",
  "facility",
  "admin",
  "patient",
  "regional_admin",
  "global-tci-manager",
  // fde-t — deferred (Drift #3)
];

function normalizeQuery(text) {
  return String(text || "")
    .trim()
    .toLowerCase();
}

function rowMatchesQuery(row, query) {
  if (!query) return true;
  const haystack = [
    row.title,
    row.bodyText,
    ...(Array.isArray(row.tags) ? row.tags : []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

/**
 * @param {string} contentId
 * @returns {Promise<object|null>}
 */
export async function getHelpContent(contentId) {
  if (!contentId) return null;
  try {
    const snap = await db.collection("helpContent").doc(contentId).get();
    if (!snap.exists) return null;
    const data = snap.data();
    if (data.active === false) return null;
    return { id: snap.id, ...data };
  } catch (err) {
    console.warn("[helpContentService] load failed", contentId, err);
    return null;
  }
}

/**
 * @param {string} role — one of HELP_CONTENT_ROLES
 * @returns {Promise<object[]>}
 */
export async function listHelpContentForRole(role) {
  if (!role) return [];
  try {
    const snap = await db
      .collection("helpContent")
      .where("role", "==", role)
      .where("active", "==", true)
      .limit(60)
      .get();
    return snap.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => String(a.title || "").localeCompare(String(b.title || "")));
  } catch (err) {
    console.warn("[helpContentService] list failed", role, err);
    return [];
  }
}

/**
 * Retrieve-only search: tag/title/body substring match (no vector search).
 *
 * @param {string} role
 * @param {string} queryText
 * @returns {Promise<object[]>}
 */
export async function searchHelpContent(role, queryText) {
  const query = normalizeQuery(queryText);
  const rows = await listHelpContentForRole(role);
  if (!query) return rows;
  return rows.filter((row) => rowMatchesQuery(row, query));
}

/**
 * Topics scoped to a workflowId (page context from HelpWorkflowContext).
 *
 * @param {string} role
 * @param {string|null} workflowId
 * @returns {Promise<object[]>}
 */
export async function listHelpContentForWorkflow(role, workflowId) {
  if (!workflowId) return listHelpContentForRole(role);
  const rows = await listHelpContentForRole(role);
  const scoped = rows.filter(
    (row) => row.workflowId === workflowId || row.linkedVideoId === workflowId,
  );
  return scoped.length ? scoped : rows;
}
